import React from 'react';
import { formatPriceDisplay } from '@/utils/currency';

interface Property {
  id: string;
  title: string;
  description?: string | null;
  property_type: string;
  listing_type?: string;
  price?: number | null;
  price_min?: number | null;
  price_max?: number | null;
  area_sqft?: number | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  location?: string | null;
  locality?: string | null;
  city?: string | null;
  state?: string | null;
  address?: string | null;
  images?: string[] | null;
  amenities?: string[] | null;
  developer_name?: string | null;
  project_name?: string | null;
  rera_id?: string | null;
  slug?: string | null;
  created_at?: string;
  updated_at?: string;
  latitude?: number | null;
  longitude?: number | null;
  furnishing?: string | null;
}

interface PropertySEOSafeProps {
  property: Property;
  canonicalUrl: string;
}

export const PropertySEOSafe: React.FC<PropertySEOSafeProps> = ({ property, canonicalUrl }) => {
  React.useEffect(() => {
    const safe = (value: any) => (value == null ? '' : String(value));
    const loc = safe(property.location || property.locality);
    const city = safe(property.city);
    const bhk = property.bedrooms ? `${property.bedrooms} BHK` : '';
    const propType = safe(property.property_type).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    const projectPart = property.project_name ? safe(property.project_name) : safe(property.title);
    const priceStr = formatPriceDisplay({ price: property.price, price_min: property.price_min ?? null, price_max: property.price_max ?? null });

    // Rich title: "Project Name | 3 BHK Apartment | Khar West, Mumbai | ₹2.5 Cr | MNRE"
    const titleSegments = [
      projectPart,
      [bhk, propType].filter(Boolean).join(' '),
      loc ? `${loc}${city ? `, ${city}` : ''}` : city || '',
      priceStr !== 'Price on Request' ? priceStr : '',
    ].filter(Boolean);
    const title = `${titleSegments.join(' | ')} | MNRE`.substring(0, 70);
    document.title = title;

    // Rich meta description - prioritize property description/about
    const areaStr = property.area_sqft ? `${property.area_sqft} sq ft` : '';
    const descParts = [
      bhk ? `${bhk} ${propType}` : propType,
      loc ? `in ${loc}${city ? `, ${city}` : ''}` : '',
      priceStr !== 'Price on Request' ? `priced at ${priceStr}` : '',
      areaStr,
      property.developer_name ? `by ${safe(property.developer_name)}` : '',
      property.furnishing ? `${safe(property.furnishing)} furnished` : '',
      property.rera_id ? `RERA: ${safe(property.rera_id)}` : '',
    ].filter(Boolean);
    const description = property.description
      ? safe(property.description).replace(/\s+/g, ' ').trim().substring(0, 155)
      : descParts.join(' • ').substring(0, 155);

    // Helper to set meta tags
    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('name', 'description', description);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', safe(canonicalUrl));
    setMeta('property', 'og:type', 'article');
    setMeta('property', 'og:site_name', 'MNRE - Mohnish Nagpal Real Estate');
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);

    if (property.images?.[0]) {
      setMeta('property', 'og:image', safe(property.images[0]));
      setMeta('name', 'twitter:image', safe(property.images[0]));
    }

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', safe(canonicalUrl));

    // JSON-LD: RealEstateListing schema
    // Map property_type to a standard schema.org type for better rich-result eligibility
    const ptype = safe(property.property_type).toLowerCase();
    const schemaType = ptype.includes('villa') || ptype.includes('house') || ptype.includes('bungalow')
      ? 'House'
      : ptype.includes('commercial') || ptype.includes('office') || ptype.includes('shop') || ptype.includes('retail')
        ? 'Product'
        : 'Apartment';

    const jsonLd: Record<string, any> = {
      '@context': 'https://schema.org',
      '@type': schemaType,
      name: safe(property.title),
      description: description,
      url: safe(canonicalUrl),
      datePosted: property.created_at,
      dateModified: property.updated_at || property.created_at,
    };

    if (property.images?.length) {
      jsonLd.image = property.images.slice(0, 5);
    }

    if (property.price) {
      jsonLd.offers = {
        '@type': 'Offer',
        price: property.price,
        priceCurrency: 'INR',
        availability: 'https://schema.org/InStock',
      };
    }

    // Embedded Place
    const addressParts = [loc, city, safe(property.state)].filter(Boolean);
    if (addressParts.length) {
      jsonLd.contentLocation = {
        '@type': 'Place',
        name: addressParts.join(', '),
        address: {
          '@type': 'PostalAddress',
          addressLocality: loc || city,
          addressRegion: safe(property.state) || 'Maharashtra',
          addressCountry: 'IN',
          ...(property.address ? { streetAddress: safe(property.address) } : {}),
        },
      };
      if (property.latitude && property.longitude) {
        jsonLd.contentLocation.geo = {
          '@type': 'GeoCoordinates',
          latitude: property.latitude,
          longitude: property.longitude,
        };
      }
    }

    // Additional property details
    if (property.amenities?.length) {
      jsonLd.amenityFeature = property.amenities.map(a => ({
        '@type': 'LocationFeatureSpecification',
        name: a,
        value: true,
      }));
    }

    // BreadcrumbList
    const breadcrumbJsonLd = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${window.location.origin}/` },
        { '@type': 'ListItem', position: 2, name: 'Properties', item: `${window.location.origin}/properties` },
        ...(loc ? [{ '@type': 'ListItem', position: 3, name: loc, item: `${window.location.origin}/properties-in/${encodeURIComponent(loc.toLowerCase())}` }] : []),
        { '@type': 'ListItem', position: loc ? 4 : 3, name: safe(property.title), item: safe(canonicalUrl) },
      ],
    };

    // RealEstateAgent
    const agentJsonLd = {
      '@context': 'https://schema.org',
      '@type': 'RealEstateAgent',
      name: 'Mohnish Nagpal Real Estate',
      url: window.location.origin,
      telephone: '+917021011304',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Mumbai',
        addressRegion: 'Maharashtra',
        addressCountry: 'IN',
      },
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify([jsonLd, breadcrumbJsonLd, agentJsonLd]);
    script.id = 'property-seo-jsonld';
    // Remove old
    document.getElementById('property-seo-jsonld')?.remove();
    document.head.appendChild(script);

    return () => {
      document.getElementById('property-seo-jsonld')?.remove();
    };
  }, [property, canonicalUrl]);

  return null;
};
